import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';
import ContactForm from '../components/contact/ContactForm';
import ContactInfo from '../components/contact/ContactInfo';
import { Phone, Clock, MapPin, Award, Wrench, Droplet, CheckCircle, XCircle, DollarSign, Shield, AlertTriangle, Camera, Home, Users, Gauge, ShieldCheck, UserCheck } from 'lucide-react';
import { SchemaInjector, createLocalBusinessSchema, createServiceSchema, createBreadcrumbSchema } from '../components/SchemaInjector';

const SewerReplacementSpotRepairPricing = () => {
  // BirdEye widget loading
  useEffect(() => {
    const loadBirdEyeWidget = () => {
      // Check if script already exists
      const existingScript = document.querySelector('#birdeye-widget-script');
      if (existingScript) return;

      // Create and load the BirdEye script
      const script = document.createElement('script');
      script.id = 'birdeye-widget-script';
      script.type = 'text/javascript';
      script.src = 'https://birdeye.com/embed/v7/172384402926614/8/9876543218815183';
      script.async = true;
      
      script.onload = () => {
        console.log('BirdEye widget loaded successfully');
      };
      
      script.onerror = () => {
        console.error('Failed to load BirdEye widget');
        // Show fallback content
        const fallback = document.querySelector('#birdeye-fallback');
        if (fallback) fallback.style.display = 'block';
      };

      document.head.appendChild(script);
    };

    loadBirdEyeWidget();

    // Cleanup on unmount
    return () => {
      const script = document.querySelector('#birdeye-widget-script');
      if (script) script.remove();
    };
  }, []);

  // SEO Meta Tags
  useEffect(() => {
    document.title = "Denver Sewer Repair Cost: $5k Spot Repair, $12k Replacement | Colorado First Plumbing";
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', 'See exact, flat-rate pricing for sewer repair in Denver. Yard Spot Repair: $5,000. Full Replacement: $12,000. 360 Google Reviews. No hidden fees ever.');
    }
  }, []);

  // Unified Schema for the entire page
  const completePageSchema = {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "WebPage",
        "@id": "https://colorado-plumbing.com/sewer-replacement-spot-repair-pricing#webpage",
        "url": "https://colorado-plumbing.com/sewer-replacement-spot-repair-pricing",
        "name": "Denver Sewer Repair Cost: $5k Spot Repair, $12k Replacement | Colorado First Plumbing",
        "description": "See exact, flat-rate pricing for sewer repair in Denver. Yard Spot Repair: $5,000. Full Replacement: $12,000. 360 Google Reviews. No hidden fees ever.",
        "isPartOf": { "@id": "https://colorado-plumbing.com/#website" },
        "breadcrumb": { "@id": "https://colorado-plumbing.com/sewer-replacement-spot-repair-pricing#breadcrumb" }
      },
      {
        "@type": "BreadcrumbList",
        "@id": "https://colorado-plumbing.com/sewer-replacement-spot-repair-pricing#breadcrumb",
        "itemListElement": [
          { "@type": "ListItem", "position": 1, "name": "Home", "item": "https://colorado-plumbing.com/" },
          { "@type": "ListItem", "position": 2, "name": "Sewer Repair Pricing", "item": "https://colorado-plumbing.com/sewer-replacement-spot-repair-pricing" }
        ]
      },
      {
        "@type": "Plumber",
        "@id": "https://colorado-plumbing.com/",
        "name": "Colorado First Plumbing and Sewer LLC",
        "image": "https://colorado-plumbing.com/colorado-plumbing-uploads/brand/Colorado-First-Plumbing-Logo-Transparent.png",
        "address": {
          "@type": "PostalAddress",
          "streetAddress": "5108 Sydney Ave", "addressLocality": "Littleton", "addressRegion": "CO", "postalCode": "80130", "addressCountry": "US"
        },
        "url": "https://colorado-plumbing.com/sewer-replacement-spot-repair-pricing",
        "aggregateRating": { "@type": "AggregateRating", "ratingValue": "4.8", "reviewCount": "360" },
        "areaServed": [
          { "@type": "City", "name": "Denver" },
          { "@type": "AdministrativeArea", "name": "Denver Metro" }
        ]
      },
      {
        "@type": "Product",
        "name": "Sewer Camera Inspection & Locate Service",
        "description": "A required first step for all sewer repairs. Our $175 professional high-definition video inspection includes locating the exact problem area. This fee is deducted from your final repair invoice.",
        "sku": "SEWER-CAM-INSPECT",
        "offers": { "@type": "Offer", "url": "https://colorado-plumbing.com/sewer-replacement-spot-repair-pricing#camera-inspection", "priceCurrency": "USD", "price": "175", "availability": "https://schema.org/InStock" },
        "aggregateRating": { "@type": "AggregateRating", "ratingValue": "4.8", "reviewCount": "360" }
      },
      {
        "@type": "Product",
        "name": "Sewer Spot Repair in Yard",
        "description": "Our $5,000 flat-rate sewer spot repair includes all permits, excavation, repair of up to 10 feet of pipe, proper pipe bedding, backfill",
        "sku": "SEWER-SPOT-YARD",
        "offers": { "@type": "Offer", "url": "https://colorado-plumbing.com/sewer-replacement-spot-repair-pricing#spot-repair-yard", "priceCurrency": "USD", "price": "5000", "availability": "https://schema.org/InStock" },
        "aggregateRating": { "@type": "AggregateRating", "ratingValue": "4.8", "reviewCount": "360" }
      },
      {
        "@type": "Product",
        "name": "Sewer Spot Repair in Street",
        "description": "Our $9,500 flat-rate price for street repairs includes all permits, traffic control, professional excavation, pipe repair up to 10 feet, and complete restoration of the street to municipal standards.",
        "sku": "SEWER-SPOT-STREET",
        "offers": { "@type": "Offer", "url": "https://colorado-plumbing.com/sewer-replacement-spot-repair-pricing#spot-repair-street", "priceCurrency": "USD", "price": "9500", "availability": "https://schema.org/InStock" },
        "aggregateRating": { "@type": "AggregateRating", "ratingValue": "4.8", "reviewCount": "360" }
      },
      {
        "@type": "Product",
        "name": "Full Sewer Line Replacement",
        "description": "A complete sewer line replacement from the home's foundation to the city tap for $12,000. This price covers up to 70 feet of pipe and includes a new double clean-out, all permits, excavation",
        "sku": "SEWER-FULL-REPLACE",
        "offers": { "@type": "Offer", "url": "https://colorado-plumbing.com/sewer-replacement-spot-repair-pricing#full-replacement", "priceCurrency": "USD", "price": "12000", "availability": "https://schema.org/InStock" },
        "aggregateRating": { "@type": "AggregateRating", "ratingValue": "4.8", "reviewCount": "360" }
      },
      {
        "@type": "Product",
        "name": "Under-Slab Sewer Spot Repair (up to 5 ft)",
        "description": "Interior sewer spot repair under a concrete slab for $2,500. This service includes careful excavation, repair of up to 5 feet of pipe, and concrete patch restoration. Does not include repair of finish flooring.",
        "sku": "SEWER-UNDERSLAB-5FT",
        "offers": { "@type": "Offer", "url": "https://colorado-plumbing.com/sewer-replacement-spot-repair-pricing#under-slab-repair", "priceCurrency": "USD", "price": "2500", "availability": "https://schema.org/InStock" },
        "aggregateRating": { "@type": "AggregateRating", "ratingValue": "4.8", "reviewCount": "360" }
      },
      {
        "@type": "Product",
        "name": "Under-Slab Sewer Spot Repair (up to 10 ft)",
        "description": "Interior sewer spot repair under a concrete slab for $5,000. This service includes careful excavation, repair of up to 10 feet of pipe, and concrete patch restoration. Does not include repair of finish flooring.",
        "sku": "SEWER-UNDERSLAB-10FT",
        "offers": { "@type": "Offer", "url": "https://colorado-plumbing.com/sewer-replacement-spot-repair-pricing#under-slab-repair", "priceCurrency": "USD", "price": "5000", "availability": "https://schema.org/InStock" },
        "aggregateRating": { "@type": "AggregateRating", "ratingValue": "4.8", "reviewCount": "360" }
      },
      {
        "@type": "Product",
        "name": "Full Under-Slab Drainage Replacement",
        "description": "Complete replacement of all drainage piping under the home's slab for $12,000. This is a comprehensive solution for homes with widespread under-slab pipe failure. Does not include repair of finish flooring.",
        "sku": "SEWER-UNDERSLAB-FULL",
        "offers": { "@type": "Offer", "url": "https://colorado-plumbing.com/sewer-replacement-spot-repair-pricing#under-slab-repair", "priceCurrency": "USD", "price": "12000", "availability": "https://schema.org/InStock" },
        "aggregateRating": { "@type": "AggregateRating", "ratingValue": "4.8", "reviewCount": "360" }
      },
      {
        "@type": "FAQPage",
        "mainEntity": [
          {
            "@type": "Question",
            "name": "What is included in the $5,000 sewer spot repair price in Denver?",
            "acceptedAnswer": { "@type": "Answer", "text": "Our $5,000 flat-rate price for a yard spot repair in Denver includes all necessary permits, professional excavation, high-quality pipe and materials for the repair, proper pipe bedding for stability, backfill of the excavated area It's an all-inclusive price with no hidden fees." }
          },
          {
            "@type": "Question",
            "name": "Why is a sewer camera inspection required before repair?",
            "acceptedAnswer": { "@type": "Answer", "text": "A $175 sewer camera inspection is essential to accurately diagnose the exact location and cause of the sewer problem. This prevents unnecessary, expensive digging and ensures we provide the correct, most cost-effective solution. The $175 fee is fully deducted from your repair invoice." }
          },
          {
            "@type": "Question",
            "name": "Is a full sewer replacement better than a spot repair?",
            "acceptedAnswer": { "@type": "Answer", "text": "For older pipes (50+ years) or lines with multiple issues, a full replacement for $12,000 is often the best long-term investment, providing a lifespan of 50-100 years. A spot repair ($5,000) is ideal for fixing a single, isolated issue in an otherwise healthy pipe." }
          },
          {
            "@type": "Question",
            "name": "What is the cost for repairing a sewer line under a concrete slab?",
            "acceptedAnswer": { "@type": "Answer", "text": "We offer transparent pricing for under-slab repairs: $2,500 for up to 5 feet, $5,000 for up to 10 feet, and $12,000 for a complete under-slab drainage replacement. These prices include excavation and concrete patching, but not the replacement of finish flooring like tile or carpet." }
          }
        ]
      }
    ]
  };

  // Smooth scroll to contact form
  const scrollToContact = () => {
    document.getElementById('contact')?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-neutral-light to-trust-light/20">
      <Header />
      <main>
        {/* Hero Section - AFFORDABLE MESSAGING */}
        <section className="relative pt-48 pb-16 bg-gradient-to-br from-trust-primary via-authority-navy to-action-primary">
          <div className="absolute inset-0 opacity-10 bg-[radial-gradient(#FFFFFF_1px,transparent_1px)] [background-size:32px_32px]"></div>
          <div className="container mx-auto px-4 relative z-10">
            <div className="max-w-4xl mx-auto text-center">
              {/* Trust Signals Banner */}
              <div className="inline-block px-6 py-3 mb-6 rounded-full bg-white/10 backdrop-blur-sm border border-white/20">
                              </div>
              
              <h1 className="text-4xl md:text-6xl font-bold mb-6 text-white leading-tight">
                Denver Metro's ONLY Affordable Sewer Contractor with Exact Online Prices
                <span className="block text-2xl md:text-3xl mt-4 text-blue-200">
                  Colorado First Plumbing and Sewer LLC
                </span>
              </h1>
              <p className="text-xl mb-8 text-blue-100 max-w-3xl mx-auto">
                How can others claim "affordable" without showing prices? We prove affordable means transparent.
                See every exact price for your sewer repair online before you call. No hidden fees, no salesmen.
              </p>
              
              {/* Clickable Price Preview Cards */}
              <div className="flex flex-wrap justify-center gap-4 mb-8">
                <a href="#spot-repair-yard" className="bg-white/10 backdrop-blur-sm rounded-lg px-4 py-2 border border-white/20 hover:bg-white/20 transition-all">
                  <span className="text-yellow-300 font-bold">Yard Spot Repair: $5,000</span>
                </a>
                <a href="#spot-repair-street" className="bg-white/10 backdrop-blur-sm rounded-lg px-4 py-2 border border-white/20 hover:bg-white/20 transition-all">
                  <span className="text-yellow-300 font-bold">Street Spot Repair: $9,500</span>
                </a>
                <a href="#under-slab-repair" className="bg-white/10 backdrop-blur-sm rounded-lg px-4 py-2 border border-white/20 hover:bg-white/20 transition-all">
                  <span className="text-yellow-300 font-bold">Under-Slab: $2,500-$5,000</span>
                </a>
                <a href="#full-replacement" className="bg-white/10 backdrop-blur-sm rounded-lg px-4 py-2 border border-white/20 hover:bg-white/20 transition-all">
                  <span className="text-yellow-300 font-bold">Full Replacement: $12,000</span>
                </a>
              </div>
              
              {/* Primary CTA - 3 Buttons */}
              <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
                
                
                <button
                  onClick={scrollToContact}
                  className="bg-gradient-to-r from-yellow-500 to-yellow-600 hover:shadow-xl hover:scale-[1.02] text-gray-900 px-8 py-4 rounded-full text-xl font-semibold inline-block transition-all duration-300"
                >
                  📝 Submit Form
                </button>
              </div>
            </div>
          </div>
        </section>

        {/* Trust Signals Section */}
        <section className="py-12 bg-gradient-to-bl from-blue-900 to-blue-950">
          <div className="container mx-auto px-4">
            <div className="grid grid-cols-2 md:grid-cols-5 gap-6 max-w-6xl mx-auto">
              <div className="text-center bg-white/10 backdrop-blur-sm rounded-xl p-4 border border-white/20">
                <div className="text-3xl font-bold text-yellow-400 mb-1">18,000+</div>
                <p className="text-white text-sm">Jobs Completed</p>
              </div>
              <div className="text-center bg-white/10 backdrop-blur-sm rounded-xl p-4 border border-white/20">
                <div className="text-3xl font-bold text-green-400 mb-1">360</div>
                <p className="text-white text-sm">Google Reviews</p>
              </div>
              <div className="text-center bg-white/10 backdrop-blur-sm rounded-xl p-4 border border-white/20">
                <div className="text-3xl font-bold text-blue-400 mb-1">30+</div>
                <p className="text-white text-sm">Years Experience</p>
              </div>
              <div className="text-center bg-white/10 backdrop-blur-sm rounded-xl p-4 border border-white/20">
                <div className="text-3xl font-bold text-purple-400 mb-1">5 Year</div>
                              </div>
              <div className="text-center bg-white/10 backdrop-blur-sm rounded-xl p-4 border border-white/20 md:col-span-1 col-span-2">
                <div className="text-3xl font-bold text-orange-400 mb-1">ONLY</div>
                <p className="text-white text-sm">Showing Prices Online</p>
              </div>
            </div>
          </div>
        </section>

        {/* Pre-Pricing Educational Block */}
        <section className="py-16 bg-gradient-to-br from-blue-800 to-blue-900">
          <div className="container mx-auto px-4 text-center">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">How Much Does Professional Sewer Camera Inspection Cost in Denver Metro?</h2>
            <p className="text-lg text-blue-100 max-w-3xl mx-auto mb-8">
              To provide an honest, exact price and ensure we solve the correct problem, a sewer camera inspection is essential. 
              This affordable diagnostic step saves you thousands by preventing unnecessary work or incorrect repairs.
            </p>
            
            <p className="text-sm text-blue-200 mt-4">This fee is deducted from your repair invoice when you proceed with the work</p>
          </div>
        </section>

        {/* Camera Inspection Section */}
        <section id="camera-inspection" className="py-16 bg-gradient-to-bl from-blue-800 to-blue-900">
          <div className="container mx-auto px-4">
            <div className="max-w-6xl mx-auto">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
                {/* Content */}
                <div>
                  <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-8 border border-white/20">
                    <div className="flex items-center mb-6">
                      <div className="w-16 h-16 bg-gradient-to-r from-success-primary to-success-eco rounded-full flex items-center justify-center mr-4 shadow-lg">
                        <Camera size={32} className="text-white" />
                      </div>
                      <div>
                        <h3 className="text-2xl font-bold text-white mb-2">Professional Sewer Camera Inspection & Locate Service</h3>
                        <div className="text-4xl font-bold text-action-warm mb-2">$175</div>
                        <p className="text-trust-light text-sm">REQUIRED before all repairs - Fee deducted from repair invoice</p>
                      </div>
                    </div>
                    
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                      <div className="flex items-center">
                        <CheckCircle size={16} className="text-success-primary mr-3 flex-shrink-0" />
                        <span className="text-white text-sm">High-definition video inspection</span>
                      </div>
                      <div className="flex items-center">
                        <CheckCircle size={16} className="text-success-primary mr-3 flex-shrink-0" />
                        <span className="text-white text-sm">Locate exact problem areas</span>
                      </div>
                      <div className="flex items-center">
                        <CheckCircle size={16} className="text-success-primary mr-3 flex-shrink-0" />
                        <span className="text-white text-sm">Detailed video report provided</span>
                      </div>
                      <div className="flex items-center">
                        <CheckCircle size={16} className="text-success-primary mr-3 flex-shrink-0" />
                        <span className="text-white text-sm">Professional diagnosis included</span>
                      </div>
                    </div>
                    
                    <div className="text-center">
                      
                    </div>

                    {/* Camera Inspection Q&A */}
                    <div className="mt-8 space-y-6">
                      <div className="border-t border-white/20 pt-6">
                        <h4 className="text-lg font-bold text-yellow-300 mb-3">Do I need a camera inspection before repair?</h4>
                        <p className="text-blue-100">Yes, Colorado First Plumbing and Sewer LLC requires a $175 camera inspection before all repairs. This prevents unnecessary digging, identifies the exact problem location, and ensures we provide the correct solution. We handle scheduling city locates and all required inspections.</p>
                      </div>
                      <div className="border-t border-white/20 pt-6">
                        <h4 className="text-lg font-bold text-yellow-300 mb-3">What does the camera inspection include?</h4>
                        <p className="text-blue-100">Our camera inspection includes high-definition video of your sewer line, professional locate services to mark pipes, detailed report with findings, and expert recommendations. This comprehensive service saves money by preventing incorrect repairs.</p>
                      </div>
                      <div className="border-t border-white/20 pt-6">
                        <h4 className="text-lg font-bold text-yellow-300 mb-3">Can the $175 fee be applied to my repair?</h4>
                        <p className="text-blue-100">Absolutely! When you proceed with any sewer repair or replacement, the full $175 camera inspection fee is deducted from your final invoice. This makes the diagnostic service essentially free when you choose Colorado First Plumbing and Sewer LLC for your repair.</p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Images */}
                <div className="space-y-6">
                  <img
                    src="/page-specific-content/centennial-plumber/sewer-scope-double-cleanout-access.jpg"
                    alt="Sewer scope and double cleanout access for inspection"
                    className="w-full rounded-2xl shadow-xl border border-white/20"
                  />
                  <img
                    src="/page-specific-content/denver-plumber/sewer-locate-affordable-denver.JPEG"
                    alt="Affordable sewer locate service in Denver"
                    className="w-full rounded-2xl shadow-xl border border-white/20"
                  />
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Master Plumber Attribution - Moved Higher */}
        <section className="py-16 bg-gradient-to-br from-blue-800 to-blue-900">
          <div className="container mx-auto px-4 max-w-4xl">
            <div className="bg-white/10 backdrop-blur-sm rounded-xl p-8 border border-white/20">
              <div className="flex flex-col md:flex-row items-center gap-8">
                <img
                  src="/colorado-plumbing-uploads/brand/emergency-plumbing-vehicle-colorado-first-plumbing-and-sewer.png"
                  alt="Colorado First Plumbing emergency service vehicle"
                  className="w-32 h-32 rounded-full object-cover bg-white/5 border-4 border-white/20 shadow-lg"
                />
                <div className="flex-grow text-center md:text-left">
                  <h3 className="text-2xl font-bold text-white mb-1">Master Plumber - Sewer Specialist</h3>
                  <h4 className="text-xl font-semibold text-yellow-300 mb-2">Codee Jessen</h4>
                                    <div className="text-blue-100 mb-4">
                    <p>• Specialties: Sewer Repair and Replacement, Multifamily Residential, Tankless Water Heater</p>
                    <p>• Experience: 18,000+ jobs completed, 30+ years combined team experience</p>
                    <p>• Commitment: Transparent pricing with no hidden fees or surprise charges</p>
                  </div>
                  <Link to="/team/codee-jessen" className="text-yellow-300 hover:text-yellow-200 font-semibold">
                    Learn more about our Master Plumber →
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Yard Spot Repair Section */}
        <section id="spot-repair-yard" className="py-16 bg-gradient-to-br from-green-800 to-green-900">
          <div className="container mx-auto px-4 mb-8">
            <h2 className="text-3xl md:text-4xl font-bold text-white text-center">How Much Does Affordable Sewer Spot Repair Cost in Your Yard?</h2>
            <p className="text-green-200 text-lg text-center mt-4 max-w-3xl mx-auto">
              Colorado First Plumbing and Sewer LLC offers transparent $5,000 flat-rate pricing for yard sewer spot repairs - the only Denver Metro contractor showing exact costs online with no hidden fees.
            </p>
          </div>
          <div className="container mx-auto px-4">
            <div className="max-w-6xl mx-auto">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
                {/* Content */}
                <div>
                  <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-8 border border-white/20">
                    <div className="flex items-center mb-6">
                      <div className="w-16 h-16 bg-gradient-to-r from-trust-primary to-trust-deep rounded-full flex items-center justify-center mr-4 shadow-lg">
                        <MapPin size={32} className="text-white" />
                      </div>
                      <div>
                        <h3 className="text-2xl font-bold text-white mb-2">Affordable Sewer Spot Repair in Your Yard</h3>
                        <div className="text-4xl font-bold text-yellow-300 mb-2">$5,000</div>
                        <p className="text-green-200 text-sm">Targeted repair in your yard area</p>
                      </div>
                    </div>
                    
                    <div className="space-y-3 mb-6">
                      <div className="flex items-center">
                        <CheckCircle size={16} className="text-yellow-300 mr-3 flex-shrink-0" />
                        <span className="text-white text-sm">Spot repair up to 10 feet length</span>
                      </div>
                      <div className="flex items-center">
                        <CheckCircle size={16} className="text-yellow-300 mr-3 flex-shrink-0" />
                        <span className="text-white text-sm">Excavation and restoration in yard</span>
                      </div>
                      <div className="flex items-center">
                        <CheckCircle size={16} className="text-yellow-300 mr-3 flex-shrink-0" />
                        <span className="text-white text-sm">New pipe section installation</span>
                      </div>
                      <div className="flex items-center">
                        <CheckCircle size={16} className="text-yellow-300 mr-3 flex-shrink-0" />
                        <span className="text-white text-sm">Proper bedding and backfill</span>
                      </div>
                      <div className="flex items-center">
                        <CheckCircle size={16} className="text-yellow-300 mr-3 flex-shrink-0" />
                                              </div>
                    </div>
                    
                    <div className="text-center mb-6">
                      <div className="flex flex-col sm:flex-row gap-3 justify-center">
                        
                        <button
                          onClick={() => document.getElementById('contact')?.scrollIntoView({ behavior: 'smooth' })}
                          className="bg-gradient-to-r from-blue-500 to-blue-600 hover:shadow-xl hover:scale-[1.02] text-white px-6 py-3 rounded-full font-bold transition-all duration-300 inline-block"
                        >
                          📝 Fill Out Form
                        </button>
                        
                      </div>
                      <p className="text-green-200 text-sm mt-3 italic">*Price to be verified with our $175 Camera & Locate service. Fee deducted if repair/replacement is approved.</p>
                    </div>

                    {/* Yard Spot Repair Q&A */}
                    <div className="space-y-6">
                      <div className="border-t border-white/20 pt-6">
                        <h4 className="text-lg font-bold text-yellow-300 mb-3">What piping is approved for underground sewer repairs?</h4>
                        <p className="text-green-100">Colorado First Plumbing and Sewer LLC uses PVC Schedule 40 Solid, SDR-35, and HDPE for pipe bursting. Various jurisdictions have preferred approved piping and materials, which we verify before any project begins.</p>
                      </div>
                      <div className="border-t border-white/20 pt-6">
                        <h4 className="text-lg font-bold text-yellow-300 mb-3">How long does a yard spot repair take?</h4>
                        <p className="text-green-100">Most yard spot repairs are completed in 1-2 days. Day one involves excavation and pipe replacement, while day two covers backfill and basic restoration. Weather and soil conditions may affect timing.</p>
                      </div>
                      <div className="border-t border-white/20 pt-6">
                        <h4 className="text-lg font-bold text-yellow-300 mb-3">Will you restore my landscaping after repair?</h4>
                        <p className="text-green-100">We backfill and restore the excavated area with proper compaction. However, landscaping replacement (grass seed, plants, decorative materials) is the customer's responsibility. We work carefully to minimize impact on existing landscaping.</p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Images */}
                <div className="space-y-6">
                  <img
                    src="/colorado-plumbing-uploads/services/sewer-services/sewer-spot-repair.JPEG"
                    alt="Professional sewer spot repair in residential yard"
                    className="w-full rounded-2xl shadow-xl border border-white/20"
                  />
                  <img
                    src="/colorado-plumbing-uploads/locations/thornton/sewer-spot-repair-thornton.JPEG"
                    alt="Sewer spot repair work in Thornton showing professional techniques"
                    className="w-full rounded-2xl shadow-xl border border-white/20"
                  />
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Street Spot Repair Section */}
        <section id="spot-repair-street" className="py-16 bg-gradient-to-bl from-red-800 to-red-900">
          <div className="container mx-auto px-4 mb-8">
            <h2 className="text-3xl md:text-4xl font-bold text-white text-center">How Much Does Sewer Spot Repair Cost in Street Areas?</h2>
            <p className="text-red-200 text-lg text-center mt-4 max-w-3xl mx-auto">
              Street sewer repairs require additional permits, traffic control, and professional restoration - Colorado First Plumbing and Sewer LLC handles every detail with transparent $9,500 flat-rate pricing.
            </p>
          </div>
          <div className="container mx-auto px-4">
            <div className="max-w-6xl mx-auto">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
                {/* Images First */}
                <div className="space-y-6 order-2 lg:order-1">
                  <img
                    src="/colorado-plumbing-uploads/services/sewer-services/sewer-street-replacement-littleton.jpeg"
                    alt="Street sewer replacement work in Littleton"
                    className="w-full rounded-2xl shadow-xl border border-white/20"
                  />
                  <img
                    src="/colorado-plumbing-uploads/services/sewer-services/flow-fill-street-sewer-repair-denver.JPEG"
                    alt="Professional street restoration with flow fill"
                    className="w-full rounded-2xl shadow-xl border border-white/20"
                  />
                </div>

                {/* Content */}
                <div className="order-1 lg:order-2">
                  <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-8 border border-white/20">
                    <div className="flex items-center mb-6">
                      <div className="w-16 h-16 bg-gradient-to-r from-red-500 to-red-600 rounded-full flex items-center justify-center mr-4 shadow-lg">
                        <AlertTriangle size={32} className="text-white" />
                      </div>
                      <div>
                        <h3 className="text-2xl font-bold text-white mb-2">Professional Street Sewer Spot Repair</h3>
                        <div className="text-4xl font-bold text-yellow-300 mb-2">$9,500</div>
                        <p className="text-red-200 text-sm">Targeted repair in street area</p>
                      </div>
                    </div>
                    
                    <div className="space-y-3 mb-6">
                      <div className="flex items-center">
                        <CheckCircle size={16} className="text-yellow-300 mr-3 flex-shrink-0" />
                        <span className="text-white text-sm">Spot repair up to 10 feet length</span>
                      </div>
                      <div className="flex items-center">
                        <CheckCircle size={16} className="text-yellow-300 mr-3 flex-shrink-0" />
                        <span className="text-white text-sm">Street excavation and restoration</span>
                      </div>
                      <div className="flex items-center">
                        <CheckCircle size={16} className="text-yellow-300 mr-3 flex-shrink-0" />
                        <span className="text-white text-sm">Traffic control if needed</span>
                      </div>
                      <div className="flex items-center">
                        <CheckCircle size={16} className="text-yellow-300 mr-3 flex-shrink-0" />
                        <span className="text-white text-sm">All permits included</span>
                      </div>
                      <div className="flex items-center">
                        <CheckCircle size={16} className="text-yellow-300 mr-3 flex-shrink-0" />
                                              </div>
                    </div>
                    
                    <div className="text-center mb-6">
                      <div className="flex flex-col sm:flex-row gap-3 justify-center">
                        
                        <button
                          onClick={() => document.getElementById('contact')?.scrollIntoView({ behavior: 'smooth' })}
                          className="bg-gradient-to-r from-blue-500 to-blue-600 hover:shadow-xl hover:scale-[1.02] text-white px-6 py-3 rounded-full font-bold transition-all duration-300 inline-block"
                        >
                          📝 Fill Out Form
                        </button>
                        
                      </div>
                      <p className="text-red-200 text-sm mt-3 italic">*Price to be verified with our $175 Camera & Locate service. Fee deducted if repair/replacement is approved.</p>
                    </div>

                    {/* Street Spot Repair Q&A */}
                    <div className="space-y-6">
                      <div className="border-t border-white/20 pt-6">
                        <h4 className="text-lg font-bold text-yellow-300 mb-3">Do you handle permits for street work?</h4>
                        <p className="text-red-100">Yes, Colorado First Plumbing and Sewer LLC handles all permit applications, inspections, and municipal coordination for street repairs. We work with local authorities to ensure proper traffic control and restore streets to city standards.</p>
                      </div>
                      <div className="border-t border-white/20 pt-6">
                        <h4 className="text-lg font-bold text-yellow-300 mb-3">How do you restore the street after repair?</h4>
                        <p className="text-red-100">We use professional street restoration techniques including proper compaction, flow fill application where required, and coordinating with the city for permanent paving. All work meets municipal standards for long-term durability.</p>
                      </div>
                      <div className="border-t border-white/20 pt-6">
                        <h4 className="text-lg font-bold text-yellow-300 mb-3">Will there be traffic disruption during repair?</h4>
                        <p className="text-red-100">We minimize traffic impact through proper planning and coordination. When necessary, we provide professional traffic control measures and work efficiently to complete repairs. Most street repairs require 1-2 days depending on complexity.</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Full Replacement Section */}
        <section id="full-replacement" className="py-16 bg-gradient-to-br from-blue-800 to-blue-900">
          <div className="container mx-auto px-4 mb-8">
            <h2 className="text-3xl md:text-4xl font-bold text-white text-center">What is the Cost for Complete Sewer Line Replacement in Denver Metro?</h2>
                      </div>
          <div className="container mx-auto px-4">
            <div className="max-w-6xl mx-auto">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
                {/* Content */}
                <div>
                  <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-8 border border-white/20">
                    <div className="flex items-center mb-6">
                      <div className="w-16 h-16 bg-gradient-to-r from-action-primary to-action-urgent rounded-full flex items-center justify-center mr-4 shadow-lg">
                        <Wrench size={32} className="text-white" />
                      </div>
                      <div>
                        <h3 className="text-2xl font-bold text-white mb-2">Complete Affordable Sewer Line Replacement</h3>
                        <div className="text-4xl font-bold text-yellow-300 mb-2">$12,000</div>
                        <p className="text-blue-200 text-sm">Complete replacement from home to street</p>
                      </div>
                    </div>
                    
                    <div className="space-y-3 mb-6">
                      <div className="flex items-center">
                        <CheckCircle size={16} className="text-success-primary mr-3 flex-shrink-0" />
                        <span className="text-white text-sm">Complete excavation and restoration</span>
                      </div>
                      <div className="flex items-center">
                        <CheckCircle size={16} className="text-success-primary mr-3 flex-shrink-0" />
                        <span className="text-white text-sm">New sewer pipe installation (up to 70')</span>
                      </div>
                      <div className="flex items-center">
                        <CheckCircle size={16} className="text-success-primary mr-3 flex-shrink-0" />
                        <span className="text-white text-sm">Connection to city tap in street</span>
                      </div>
                      <div className="flex items-center">
                        <CheckCircle size={16} className="text-success-primary mr-3 flex-shrink-0" />
                        <span className="text-white text-sm">Double clean-out installation</span>
                      </div>
                      <div className="flex items-center">
                        <CheckCircle size={16} className="text-success-primary mr-3 flex-shrink-0" />
                                              </div>
                    </div>
                    
                    <div className="text-center mb-6">
                      <div className="flex flex-col sm:flex-row gap-3 justify-center">
                        
                        <button
                          onClick={() => document.getElementById('contact')?.scrollIntoView({ behavior: 'smooth' })}
                          className="bg-gradient-to-r from-blue-500 to-blue-600 hover:shadow-xl hover:scale-[1.02] text-white px-6 py-3 rounded-full font-bold transition-all duration-300 inline-block"
                        >
                          📝 Fill Out Form
                        </button>
                        
                      </div>
                      <p className="text-blue-200 text-sm mt-3 italic">*Price to be verified with our $175 Camera & Locate service. Fee deducted if repair/replacement is approved.</p>
                    </div>

                    {/* Full Replacement Q&A */}
                    <div className="space-y-6">
                      <div className="border-t border-white/20 pt-6">
                        <h4 className="text-lg font-bold text-yellow-300 mb-3">Is full replacement better than multiple spot repairs?</h4>
                        <p className="text-blue-100">Often yes. Colorado First Plumbing and Sewer LLC recommends full replacement for pipes over 50 years old or with multiple problem areas. New pipes last 50-100 years, while multiple spot repairs can quickly exceed replacement costs.</p>
                      </div>
                      <div className="border-t border-white/20 pt-6">
                        <h4 className="text-lg font-bold text-yellow-300 mb-3">What's included in the double cleanout installation?</h4>
                        <p className="text-blue-100">Colorado code requires double cleanouts for optimal access. We install traffic-rated cleanouts that meet municipal standards, providing easy access points for future maintenance and inspection requirements.</p>
                      </div>
                      <div className="border-t border-white/20 pt-6">
                        <h4 className="text-lg font-bold text-yellow-300 mb-3">How long does full replacement take?</h4>
                        <p className="text-blue-100">Most full sewer replacements take 2-4 days. This includes excavation, old pipe removal, new pipe installation, connection to city services, inspection, and restoration. Complex projects may require additional time.</p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Images */}
                <div className="space-y-6">
                  <img
                    src="/colorado-plumbing-uploads/services/sewer-services/excavation-replace-sewer-expert.JPEG"
                    alt="Expert sewer excavation and replacement work"
                    className="w-full rounded-2xl shadow-xl border border-white/20"
                  />
                  <img
                    src="/colorado-plumbing-uploads/locations/centennial/sewer-replacement-centennial.jpeg"
                    alt="Complete sewer replacement project in Centennial"
                    className="w-full rounded-2xl shadow-xl border border-white/20"
                  />
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Under-Slab Repair Sections */}
        <section id="under-slab-repair" className="py-16 bg-gradient-to-bl from-purple-800 to-purple-900">
          <div className="container mx-auto px-4">
            <div className="text-center mb-16">
              <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">Interior Under-Slab Sewer Repairs</h2>
              <p className="text-purple-200 text-lg max-w-3xl mx-auto">When sewer problems occur under your home's foundation, we provide careful interior repairs with dust containment and proper concrete restoration.</p>
            </div>

            {/* Under-Slab 5ft */}
            <div className="max-w-6xl mx-auto mb-16">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
                {/* Images First */}
                <div className="space-y-6 order-2 lg:order-1">
                  <img
                    src="/colorado-plumbing-uploads/services/sewer-services/basement-chipping-sewer-replacement.JPEG"
                    alt="Professional under-slab sewer replacement in basement"
                    className="w-full rounded-2xl shadow-xl border border-white/20"
                  />
                  <img
                    src="/colorado-plumbing-uploads/services/sewer-services/concrete-patch-sewer-repair-basement-littleton.JPEG"
                    alt="Concrete patch restoration after basement sewer repair"
                    className="w-full rounded-2xl shadow-xl border border-white/20"
                  />
                </div>

                {/* Content */}
                <div className="order-1 lg:order-2">
                  <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-8 border border-white/20">
                    <div className="flex items-center mb-6">
                      <div className="w-16 h-16 bg-gradient-to-r from-purple-400 to-indigo-500 rounded-full flex items-center justify-center mr-4 shadow-lg">
                        <Home size={32} className="text-white" />
                      </div>
                      <div>
                        <h4 className="text-lg font-bold text-white mb-2">Under-Slab Spot Repair (5 ft)</h4>
                        <div className="text-4xl font-bold text-yellow-300 mb-2">$2,500</div>
                        <p className="text-purple-200 text-sm">Interior repair under concrete slab</p>
                      </div>
                    </div>
                    
                    <div className="space-y-3 mb-6">
                      <div className="flex items-center">
                        <CheckCircle size={16} className="text-yellow-300 mr-3 flex-shrink-0" />
                        <span className="text-white text-sm">Careful interior excavation</span>
                      </div>
                      <div className="flex items-center">
                        <CheckCircle size={16} className="text-yellow-300 mr-3 flex-shrink-0" />
                        <span className="text-white text-sm">Repair up to 5 feet of pipe</span>
                      </div>
                      <div className="flex items-center">
                        <CheckCircle size={16} className="text-yellow-300 mr-3 flex-shrink-0" />
                        <span className="text-white text-sm">Concrete patch restoration</span>
                      </div>
                      <div className="flex items-center">
                        <AlertTriangle size={16} className="text-orange-300 mr-3 flex-shrink-0" />
                        <span className="text-white text-sm italic">Floor finishes not included</span>
                      </div>
                    </div>
                    
                    <div className="text-center mb-6">
                      <div className="flex flex-col sm:flex-row gap-3 justify-center">
                        
                        <button
                          onClick={() => document.getElementById('contact')?.scrollIntoView({ behavior: 'smooth' })}
                          className="bg-gradient-to-r from-blue-500 to-blue-600 hover:shadow-xl hover:scale-[1.02] text-white px-6 py-3 rounded-full font-bold transition-all duration-300 inline-block"
                        >
                          📝 Fill Out Form
                        </button>
                        
                      </div>
                      <p className="text-purple-200 text-sm mt-3 italic">*Price to be verified with our $175 Camera & Locate service. Fee deducted if repair/replacement is approved.</p>
                    </div>

                    {/* Under-Slab Q&A */}
                    <div className="space-y-6">
                      <div className="border-t border-white/20 pt-6">
                        <h4 className="text-lg font-bold text-yellow-300 mb-3">What's not included in under-slab pricing?</h4>
                        <p className="text-purple-100">Floor finish replacement (carpet, tile, wood flooring) is not included. We patch the concrete foundation but homeowners handle decorative flooring. We work carefully to minimize the affected area.</p>
                      </div>
                      <div className="border-t border-white/20 pt-6">
                        <h4 className="text-lg font-bold text-yellow-300 mb-3">How do you contain dust during interior work?</h4>
                        <p className="text-purple-100">Colorado First Plumbing and Sewer LLC uses professional dust containment measures including plastic sheeting, negative air machines, and careful work practices to protect your home's interior during under-slab repairs.</p>
                      </div>
                      <div className="border-t border-white/20 pt-6">
                        <h4 className="text-lg font-bold text-yellow-300 mb-3">Can belly areas qualify for under-slab repair?</h4>
                        <p className="text-purple-100">Belly areas (sagging pipe sections) may not qualify for standard under-slab repairs due to structural limitations. Our camera inspection determines feasibility and may recommend alternative solutions or full replacement.</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Under-Slab 10ft and Full Replacement Cards */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-4xl mx-auto">
              {/* 10ft Card */}
              <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-6 border border-white/20">
                <div className="text-center mb-4">
                  <div className="w-12 h-12 bg-gradient-to-r from-pink-400 to-purple-500 rounded-full flex items-center justify-center mx-auto mb-3 shadow-lg">
                    <Home size={24} className="text-white" />
                  </div>
                  <h4 className="text-lg font-bold text-white mb-2">Under-Slab Repair (10 ft)</h4>
                  <div className="text-3xl font-bold text-yellow-300 mb-2">$5,000</div>
                </div>
                
                <ul className="space-y-2 text-white text-sm mb-6">
                  <li className="flex items-center">
                    <CheckCircle size={14} className="text-yellow-300 mr-2" />
                    Up to 10 feet of pipe repair
                  </li>
                  <li className="flex items-center">
                    <CheckCircle size={14} className="text-yellow-300 mr-2" />
                    Complete concrete restoration
                  </li>
                  <li className="flex items-center">
                    <AlertTriangle size={14} className="text-orange-300 mr-2" />
                    Floor finishes not included
                  </li>
                </ul>
                
                <div className="text-center">
                  <div className="flex flex-col gap-3">
                    
                    <div className="flex gap-2">
                      <button
                        onClick={() => document.getElementById('contact')?.scrollIntoView({ behavior: 'smooth' })}
                        className="bg-gradient-to-r from-blue-500 to-blue-600 hover:shadow-xl hover:scale-[1.02] text-white px-3 py-2 rounded-full font-semibold transition-all duration-300 flex-1 text-xs"
                      >
                        📝 Form
                      </button>
                      
                    </div>
                  </div>
                  <p className="text-purple-300 text-xs mt-2 italic">*Price verified with $175 Camera & Locate</p>
                </div>
              </div>

              {/* Full Under-Slab Card */}
              <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-6 border border-white/20">
                <div className="text-center mb-4">
                  <div className="w-12 h-12 bg-gradient-to-r from-gray-500 to-gray-700 rounded-full flex items-center justify-center mx-auto mb-3 shadow-lg">
                    <Home size={24} className="text-white" />
                  </div>
                  <h4 className="text-lg font-bold text-white mb-2">Full Under-Slab Replacement</h4>
                  <div className="text-3xl font-bold text-yellow-300 mb-2">$12,000</div>
                </div>
                
                <ul className="space-y-2 text-white text-sm mb-6">
                  <li className="flex items-center">
                    <CheckCircle size={14} className="text-yellow-300 mr-2" />
                    Complete drainage replacement
                  </li>
                  <li className="flex items-center">
                    <CheckCircle size={14} className="text-yellow-300 mr-2" />
                    Code compliance upgrade
                  </li>
                  <li className="flex items-center">
                    <AlertTriangle size={14} className="text-orange-300 mr-2" />
                    Belly areas may not qualify
                  </li>
                </ul>
                
                <div className="text-center">
                  <div className="flex flex-col gap-3">
                    
                    <div className="flex gap-2">
                      <button
                        onClick={() => document.getElementById('contact')?.scrollIntoView({ behavior: 'smooth' })}
                        className="bg-gradient-to-r from-blue-500 to-blue-600 hover:shadow-xl hover:scale-[1.02] text-white px-3 py-2 rounded-full font-semibold transition-all duration-300 flex-1 text-xs"
                      >
                        📝 Form
                      </button>
                      
                    </div>
                  </div>
                  <p className="text-gray-300 text-xs mt-2 italic">*Price verified with $175 Camera & Locate</p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Guarantees Section */}
        <section className="py-16 bg-gradient-to-br from-blue-800 to-blue-900">
          <div className="container mx-auto px-4">
            <h2 className="text-3xl md:text-4xl font-bold text-center mb-12 text-white">Guaranteed Peace of Mind</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {/* Card 2: Licensed & Insured */}
              <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6 border border-white/20">
                <Award className="w-10 h-10 text-yellow-400 mb-4" />
                <h4 className="text-lg font-bold text-white mb-2">Licensed, Bonded & Insured</h4>
                <p className="text-blue-100">We are a fully licensed, bonded, and insured plumbing contractor with Master Plumber ownership. This protects you from liability and guarantees professional standards.</p>
              </div>
              {/* Card 3: No Sales Pressure */}
              <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6 border border-white/20">
                <UserCheck className="w-10 h-10 text-blue-400 mb-4" />
                <h4 className="text-lg font-bold text-white mb-2">No Salesmen, Ever</h4>
                <p className="text-blue-100">Our licensed plumbers provide expert advice, not high-pressure sales pitches. We focus on education and clear communication so you can make the best decision for your home.</p>
              </div>
            </div>
          </div>
        </section>

        {/* Why Choose Us Section - Competitive Positioning */}
        <section className="py-20 bg-gradient-to-br from-trust-deep to-authority-navy text-white">
          <div className="container mx-auto px-4">
            <div className="text-center max-w-3xl mx-auto mb-16">
              <h2 className="text-3xl md:text-4xl font-bold mb-6 text-neutral-white">
                How Can Others Claim "Affordable" Without Showing Prices?
              </h2>
              <p className="text-xl text-trust-light">
                We're the ONLY qualified, licensed, and heavily reviewed sewer contractor in Denver Metro proving affordable means transparent.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-12 items-center mb-16">
              {/* What Others Do */}
              <div>
                <h4 className="text-xl font-bold mb-6 text-red-300 flex items-center">
                  <XCircle className="mr-3" size={24} />
                  What Other Contractors Do:
                </h4>
                <div className="space-y-4">
                  <div className="bg-red-900/30 border-l-4 border-red-500 p-4 rounded-lg">
                    <p className="font-semibold text-red-200 mb-1">"We need to send someone out to take a look"</p>
                    <p className="text-red-300 text-sm">Translation: We're sending a salesman to pressure you</p>
                  </div>
                  <div className="bg-red-900/30 border-l-4 border-red-500 p-4 rounded-lg">
                    <p className="font-semibold text-red-200 mb-1">"Call for pricing" or "$500 OFF Today Only!"</p>
                    <p className="text-red-300 text-sm">$500 off what? They won't tell you the actual price</p>
                  </div>
                  <div className="bg-red-900/30 border-l-4 border-red-500 p-4 rounded-lg">
                    <p className="font-semibold text-red-200 mb-1">Hidden pricing until arrival</p>
                    <p className="text-red-300 text-sm">You're trapped once they're at your door</p>
                  </div>
                </div>
              </div>

              {/* What We Do */}
              <div>
                <h4 className="text-xl font-bold mb-6 text-success-primary flex items-center">
                  <CheckCircle className="mr-3" size={24} />
                  What We Do Different:
                </h4>
                <div className="space-y-4">
                  <div className="bg-success-primary/20 border-l-4 border-success-primary p-4 rounded-lg">
                    <p className="font-semibold text-success-light mb-1">Actual prices online right now</p>
                    <p className="text-success-light/80 text-sm">$5,000 yard repair, $12,000 full replacement, $2,500 under-slab</p>
                  </div>
                  <div className="bg-success-primary/20 border-l-4 border-success-primary p-4 rounded-lg">
                    <p className="font-semibold text-success-light mb-1">No salesmen - just skilled plumbers</p>
                    <p className="text-success-light/80 text-sm">Our techs fix problems, they don't sell you things you don't need</p>
                  </div>
                  <div className="bg-success-primary/20 border-l-4 border-success-primary p-4 rounded-lg">
                    <p className="font-semibold text-success-light mb-1">18,000+ jobs with 360 Google reviews</p>
                    <p className="text-success-light/80 text-sm">Proven track record with transparent pricing on every job</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Final CTA */}
            <div className="text-center">
              <div className="bg-gradient-to-r from-action-primary to-action-urgent rounded-2xl p-8 shadow-2xl">
                <h4 className="text-xl md:text-2xl font-bold mb-4">
                  Why Call a Plumbing Company to Receive a Salesman?
                </h4>
                <p className="text-lg mb-6 text-action-warm/90">
                  Book with us and know exactly what the price is. No hassle pricing you can see right now.
                </p>
                <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
                  
                  <a 
                    href="https://www.google.com/search?q=Colorado+First+Plumbing+and+Sewer+LLC&tbm=lcl" 
                    target="_blank"
                    rel="noopener noreferrer"
                    className="bg-white/20 hover:bg-white/30 hover:scale-[1.02] text-white px-8 py-4 rounded-full text-xl font-semibold inline-block transition-all duration-300 border-2 border-white w-full sm:w-auto"
                  >
                    ⭐ Read 360 Google Reviews
                  </a>
                </div>
                <p className="text-action-warm/80 mt-4 text-sm font-medium">
                  No other plumbing contractor in Denver Metro with this many verified reviews is willing to show you their pricing online
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Contact Form Section */}
        <section id="contact" className="relative py-24 bg-gradient-to-br from-trust-deep to-authority-navy overflow-hidden">
          {/* Modern Background Pattern */}
          <div className="absolute inset-0 opacity-10 bg-[radial-gradient(#FFFFFF_1px,transparent_1px)] [background-size:32px_32px]"></div>
          
          <div className="container mx-auto px-4 relative z-10">
            <div className="text-center max-w-3xl mx-auto mb-16">
              <div className="inline-block px-4 py-2 mb-6 rounded-full bg-trust-light text-trust-deep font-semibold text-sm border border-trust-primary/20">
                🤝 Get In Touch
              </div>
              <h3 className="text-2xl md:text-3xl font-bold mb-6 text-neutral-white">
                Ready to Solve Your Sewer Problems?
              </h3>
              <p className="text-xl text-trust-light/90">
                Contact Colorado First Plumbing for professional service, transparent pricing, and expert solutions. 
                We're here to help with all your sewer needs.
              </p>
            </div>
            
            <div className="grid grid-cols-1 lg:grid-cols-5 gap-8 items-start">
              <div className="col-span-1 lg:col-span-2 order-2 lg:order-1">
                <ContactInfo />
              </div>
              
              <div className="col-span-1 lg:col-span-3 order-1 lg:order-2">
                <ContactForm />
              </div>
            </div>
          </div>
        </section>

      </main>
      <Footer />
      
      {/* Schema Injection */}
      <SchemaInjector 
        schemas={[completePageSchema]}
        title="Denver Sewer Repair Cost: $5k Spot Repair, $12k Replacement | Colorado First Plumbing"
        description="See exact, flat-rate pricing for sewer repair in Denver. Yard Spot Repair: $5,000. Full Replacement: $12,000. 360 Google Reviews. No hidden fees ever."
        canonical="https://colorado-plumbing.com/sewer-replacement-spot-repair-pricing"
        keywords="denver sewer repair cost, sewer replacement cost denver, sewer spot repair price, under slab sewer repair, flat-rate sewer contractor"
      />
    </div>
  );
};

export default SewerReplacementSpotRepairPricing;